import React, { useEffect, useMemo, useState } from "react";
import { backendUrl } from "./config.ts";

const TEMPLATE_FALLBACK_CLIENT = "LdcuiH328WKijRn8EJ4u";

const enableLiveTranscriptPreviewMode = (html) => {
  let nextHtml = html;

  // Ensure width-preview toggle function keeps preview transcript visible without live mic activation.
  nextHtml = nextHtml.replace(
    "const newSrc = `${baseUrl}/?client=${clientId}&mode=widget&hideWarnings=true&showAvatar=true${mode === 'responsive' ? '&responsiveMic=true' : ''}`;",
    "const newSrc = `${baseUrl}/?client=${clientId}&mode=widget&hideWarnings=true&showAvatar=true&previewTranscript=true${mode === 'responsive' ? '&responsiveMic=true' : ''}`;"
  );

  const enhancer = `
<style>
.iframe-boundary-badge {
  margin: 8px 0 6px;
  display: inline-block;
  font-size: 0.74rem;
  font-weight: 700;
  color: #2e4f7f;
  background: #e8f0fd;
  border: 1px solid #b7cae6;
  border-radius: 999px;
  padding: 4px 10px;
}
</style>
<script>
(function () {
  function withPreviewParam(src) {
    try {
      const url = new URL(src, window.location.origin);
      if (url.searchParams.get('mode') === 'widget') {
        url.searchParams.set('previewTranscript', 'true');
      }
      return url.toString();
    } catch (err) {
      return src;
    }
  }

  function promoteResponsiveOption() {
    const container = document.querySelector('.width-options');
    if (!container) return;

    const responsiveFrame = container.querySelector('#my-widget-iframe-responsive');
    if (!responsiveFrame) return;

    const responsiveOption = responsiveFrame.closest('.width-option');
    if (!responsiveOption) return;

    if (container.firstElementChild !== responsiveOption) {
      container.insertBefore(responsiveOption, container.firstElementChild);
    }

    const heading = responsiveOption.querySelector('h4');
    if (heading && !heading.textContent.includes('Recommended')) {
      heading.textContent = heading.textContent + ' (Recommended Default)';
    }
  }

  function applyPreviewMode() {
    document.querySelectorAll('.width-option iframe').forEach((frame) => {
      if (!frame || !frame.src) return;

      const updated = withPreviewParam(frame.src);
      if (updated !== frame.src) {
        frame.src = updated;
      }

      if (frame.dataset.previewOutlined === 'true') return;
      frame.dataset.previewOutlined = 'true';

      frame.style.border = '2px dashed #8fb1dd';
      frame.style.borderRadius = '10px';
      frame.style.boxSizing = 'border-box';
      frame.style.background = '#f8fbff';
      frame.style.display = 'block';
      frame.style.margin = '0 auto';

      const option = frame.closest('.width-option');
      if (!option) return;

      const widthAttr = (frame.getAttribute('width') || '').trim();
      const widthLabel = widthAttr === '100%'
        ? 'container width (100%)'
        : (widthAttr || 'auto') + 'px';

      const badge = document.createElement('div');
      badge.className = 'iframe-boundary-badge';
      badge.textContent = 'Preview boundary: ' + widthLabel;
      option.insertBefore(badge, frame);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      promoteResponsiveOption();
      applyPreviewMode();
    });
  } else {
    promoteResponsiveOption();
    applyPreviewMode();
  }
})();
<\/script>`;

  if (!nextHtml.includes('</body>')) {
    return `${nextHtml}${enhancer}`;
  }

  return nextHtml.replace('</body>', `${enhancer}</body>`);
};

const IframeDemo = () => {
  const [srcDoc, setSrcDoc] = useState("");
  const [error, setError] = useState("");
  const [routeClientId, setRouteClientId] = useState(TEMPLATE_FALLBACK_CLIENT);
  const [layoutInfo, setLayoutInfo] = useState(null);
  const [returnTo, setReturnTo] = useState("admin");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const activeClientId = urlParams.get("client") || TEMPLATE_FALLBACK_CLIENT;
    const nextReturnTo = urlParams.get("returnTo") === "client" ? "client" : "admin";
    setRouteClientId(activeClientId);
    setReturnTo(nextReturnTo);

    Promise.all([
      fetch("/iframe-demo-template.txt", { cache: "no-store" }).then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load template: ${res.status}`);
        }
        return res.text();
      }),
      fetch(`${backendUrl}/api/client-config?client=${activeClientId}`, { cache: "no-store" })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ])
      .then(([html, clientConfig]) => {
        let nextHtml = html;
        const widgetLayout = clientConfig?.ui?.layout?.widget || null;
        setLayoutInfo(widgetLayout);

        nextHtml = nextHtml.split(TEMPLATE_FALLBACK_CLIENT).join(activeClientId);
        nextHtml = nextHtml.replace(
          /const response = await fetch\(`\$\{baseUrl\}\/api\/client-config\?client=\$\{clientId\}`\);/,
          `const response = await fetch('${backendUrl}/api/client-config?client=' + clientId);`
        );
        nextHtml = nextHtml.split("https://your-domain.com").join(window.location.origin);
        nextHtml = nextHtml.split("YOUR_CLIENT_ID").join(activeClientId);
        nextHtml = nextHtml.split("Small Widget (300px)").join("Iframe Width: 300px");
        nextHtml = nextHtml.split("Medium Widget (500px)").join("Iframe Width: 500px");


        const mode = widgetLayout?.mode;
        if (mode === "fixed") {
          nextHtml = nextHtml.split("Fixed Mic").join("Current Mode: Fixed");
          nextHtml = nextHtml.split("Responsive Mic").join("Preview Responsive Mode");
        } else if (mode === "responsive") {
          nextHtml = nextHtml.split("Responsive Mic").join("Current Mode: Responsive");
          nextHtml = nextHtml.split("Fixed Mic").join("Preview Fixed Mode");
        }

        nextHtml = enableLiveTranscriptPreviewMode(nextHtml);
        setSrcDoc(nextHtml);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || "Failed to load iframe guide");
      });
  }, []);

  const layoutSummary = useMemo(() => {
    if (!layoutInfo) {
      return {
        modeLabel: "Unknown",
        detail: "Unable to read the saved widget sizing settings for this client.",
      };
    }

    if (layoutInfo.mode === "fixed") {
      return {
        modeLabel: "Fixed",
        detail: `Current fixed mic size: ${layoutInfo.size || "Not set"}px`,
      };
    }

    return {
      modeLabel: "Responsive",
      detail: `Current responsive range: ${layoutInfo.min || "?"}px to ${layoutInfo.max || "?"}px`,
    };
  }, [layoutInfo]);

  if (error) {
    return (
      <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
        <h1>Iframe Guide Unavailable</h1>
        <div>{error}</div>
      </div>
    );
  }

  if (!srcDoc) {
    return (
      <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
        Loading iframe guide...
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#eef3fb", fontFamily: "system-ui, sans-serif", position: "relative" }}>
      <div
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          width: "min(360px, calc(100vw - 24px))",
          background: "rgba(255, 255, 255, 0.96)",
          border: "1px solid rgba(216, 226, 240, 0.95)",
          borderRadius: 12,
          padding: "12px 14px",
          boxShadow: "0 10px 28px rgba(25, 42, 70, 0.16)",
          zIndex: 20,
          backdropFilter: "blur(8px)",
        }}
      >
        <div style={{ fontSize: "0.76rem", color: "#6a7c95", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
          Widget sizing
        </div>
        <div style={{ fontSize: "1rem", fontWeight: 700, color: "#24324a", marginBottom: 2 }}>
          Current Mode: {layoutSummary.modeLabel}
        </div>
        <div style={{ color: "#52637a", fontSize: "0.92rem", marginBottom: 8 }}>{layoutSummary.detail}</div>
        <div style={{ color: "#6b7a90", fontSize: "0.82rem", lineHeight: 1.35, marginBottom: 8 }}>
          Width previews below now use the live widget UI with transcript controls visible in preview mode.
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
          <a
            href={returnTo === "client" ? `/client/${encodeURIComponent(routeClientId)}` : `/admin?editClient=${encodeURIComponent(routeClientId)}`}
            style={{ color: "#0066cc", fontWeight: 600, fontSize: "0.88rem" }}
          >
            Change sizing
          </a>
          <span style={{ color: "#6b7a90", fontSize: "0.76rem", textAlign: "right" }}>
            Alternate mode below is preview only.
          </span>
        </div>
      </div>

      <iframe
        title="Iframe Embedding Guide"
        srcDoc={srcDoc}
        style={{
          width: "100%",
          height: "100vh",
          minHeight: 900,
          border: "none",
          display: "block",
          background: "#eef3fb",
        }}
      />
    </div>
  );
};

export default IframeDemo;






